import { useCallback, useMemo, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import clsx from 'clsx';
import QueuePage from '../../components/ui/QueuePage';
import { Toast } from '../../components/ui/Toast';
import DispositionModal from '../../components/returns/DispositionModal';
import { useReturnDisposition } from '../../hooks/useReturnDisposition';
import { useApp } from '../../hooks/useApp';

interface DispositionRow {
  id: string;
  return_number?: string | null;
  isg_sku?: string | null;
  title?: string | null;
  condition_received?: string | null;
  disposition?: string | null;
  quantity?: number | null;
  distributor_name?: string | null;
  updated_at?: string | null;
}

const DISP_CLS: Record<string, string> = {
  return_to_distributor: 'bg-warning/15 text-warning',
  relist_new: 'bg-success/15 text-success',
  relist_used: 'bg-success/15 text-success',
  relist_open_box: 'bg-success/15 text-success',
  send_to_fba: 'bg-info/15 text-info',
  dispose: 'bg-danger/15 text-danger',
  write_off: 'bg-danger/15 text-danger',
  dispute_marketplace: 'bg-accent/15 text-accent',
};
const DISP_LABEL: Record<string, string> = {
  return_to_distributor: 'Return to Dist.',
  relist_new: 'Relist (New)',
  relist_used: 'Relist (Used)',
  relist_open_box: 'Relist (Open Box)',
  send_to_fba: 'Send to FBA',
  dispose: 'Dispose',
  write_off: 'Write Off',
  dispute_marketplace: 'Dispute',
};

function relativeTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ReturnsDisposition(): JSX.Element {
  const navigate = useNavigate();
  // Day 64.5 V-ROUI: viewer + dist_rep read-only.
  const { isViewer, isDistRep } = useApp();
  // Cluster 8 (Rule 10): admin/user can set a disposition by clicking a row.
  const disp = useReturnDisposition();
  const canDisposition = !isViewer && !isDistRep;

  const SkuCell = useCallback(
    (p: ICellRendererParams<DispositionRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (sku) navigate(`/catalog/products/${sku}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate]
  );

  const DispCell = useCallback((p: ICellRendererParams<DispositionRow>): JSX.Element => {
    const disp = p.data?.disposition;
    if (!disp)
      return (
        <span className="text-ink-muted px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-3">
          Pending
        </span>
      );
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium',
          DISP_CLS[disp] || 'bg-surface-3 text-ink-muted'
        )}
      >
        {DISP_LABEL[disp] || disp}
      </span>
    );
  }, []);

  const columns = useMemo<ColDef<DispositionRow>[]>(
    () =>
      [
        {
          suppressMovable: true,
          resizable: false,
          width: 40,
          maxWidth: 40,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Return #',
          field: 'return_number',
          width: 130,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Condition',
          field: 'condition_received',
          width: 100,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Disposition',
          field: 'disposition',
          width: 140,
          cellRenderer: DispCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Qty',
          field: 'quantity',
          width: 70,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Distributor',
          field: 'distributor_name',
          width: 130,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Updated',
          field: 'updated_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<DispositionRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<DispositionRow>[],
    [SkuCell, DispCell]
  );

  return (
    <>
      <QueuePage
        title="Disposition"
        rpcName="get_returns_queue"
        rpcParams={{ p_status: 'disposition' }}
        columns={columns}
        statusTabs={[
          { key: 'all', label: 'All' },
          { key: 'inspection', label: 'Pending Decision' },
          { key: 'disposition', label: 'Decision Made' },
        ]}
        emptyIcon={RotateCcw}
        emptyMessage="No returns awaiting disposition"
        searchPlaceholder="Search return #, SKU, or title..."
        readOnly={isViewer || isDistRep}
        onRowClicked={canDisposition ? (d) => disp.open(d as DispositionRow) : undefined}
        refreshSignal={disp.refreshSignal}
      />
      <DispositionModal
        open={!!disp.target}
        item={disp.target}
        onConfirm={disp.confirm}
        onCancel={disp.cancel}
        submitting={disp.submitting}
        error={disp.error}
      />
      <Toast toast={disp.toast} onDismiss={() => disp.setToast(null)} />
    </>
  );
}
